import {
  Box,
  Dialog,
  IconButton,
  ListItemIcon,
  MenuItem,
  Select,
  Typography,
  useTheme,
} from '@mui/material';
import { ReactComponent as InfoIcon } from '../../svgIcons/infoicon.svg';
import { ReactComponent as ImportantIcon } from '../../svgIcons/important.svg';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useState } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import {
  GetLocationReportInfoDocument,
  InfoType,
  useAddLocationReportInfoMutation,
} from '../../generated/graphql';
import { SelectDate } from '../SelectDate';
import TagAutocomplete from '../common/TagAutocomplete';

interface ReportToTerminalAddInfoModalProps {
  handleClose: () => void;
  open: boolean;
  locationId: number;
  terminalName: string;
  dateForAddInfo: string;
  dayForAddInfo: string;
  isForTerminal: boolean;
}

export function ReportToTerminalAddInfoModal(
  props: ReportToTerminalAddInfoModalProps,
) {
  const {
    open,
    handleClose,
    locationId,
    terminalName,
    dateForAddInfo,
    dayForAddInfo,
    isForTerminal,
  } = props;
  const { t } = useTranslation();

  const titleString =
    terminalName && dayForAddInfo && dateForAddInfo
      ? dayForAddInfo
          .concat(' ')
          .concat(dateForAddInfo)
          .concat(' | ')
          .concat(terminalName)
      : '';
  const theme = useTheme();
  const lessThan1400 = useMediaQuery(theme.breakpoints.between(0, 1400));
  // Local state for tags; not sent to backend yet
  const [tags, setTags] = useState<string[]>([]);
  const [addInternalTerminalReportInfoMutation] =
    useAddLocationReportInfoMutation({
      refetchQueries: [
        {
          query: GetLocationReportInfoDocument,
          variables: {
            locationId,
            dateForAddInfo,
            forTerminal: isForTerminal,
          },
        },
      ],
    });

  if (!locationId || !dateForAddInfo) {
    return <></>;
  }
  return (
    <Dialog
      maxWidth={lessThan1400 ? 'sm' : 'md'}
      fullWidth={true}
      PaperProps={{
        sx: {
          height: '100%',
        },
      }}
      sx={{
        height: '100%',
      }}
      open={open}
      onClose={handleClose}
    >
      <Formik
        initialValues={{
          infoType: InfoType.Info,
          info: '',
          locationId,
          dateForAddInfo,
          createUntilDate: dateForAddInfo,
          isAddedFromTerminal: isForTerminal,
        }}
        onSubmit={async (values: {
          infoType: InfoType;
          info: string;
          locationId: number;
          dateForAddInfo: string;
          createUntilDate: string;
          isAddedFromTerminal: boolean;
        }) => {
          addInternalTerminalReportInfoMutation({
            variables: {
              input: { ...values },
            },
          });
          handleClose();
        }}
      >
        {(props) => (
          <form
            onSubmit={props.handleSubmit}
            style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
          >
            <DialogTitle>
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                width="100%"
                mb={2}
              >
                <Typography variant="body1">
                  <strong>{`${t('actions.add', {
                    item: t('attributes.info'),
                  })}`}</strong>
                </Typography>
                <Typography variant="body1">{titleString}</Typography>
                <IconButton style={{ padding: '0px' }} onClick={handleClose}>
                  <CloseIcon fontSize="medium" color="action" />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  paddingBottom: '1%',
                  columnGap: 2,
                  rowGap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Typography variant="body1">{t('attributes.type')}</Typography>
                <Select
                  variant="standard"
                  name="infoType"
                  id="infoType"
                  autoWidth={true}
                  defaultValue={InfoType.Info}
                  value={props.values.infoType}
                  onChange={props.handleChange}
                >
                  <MenuItem value={InfoType.Important}>
                    <ListItemIcon
                      style={{
                        width: '1em',
                        height: '1em',
                        fontSize: '1.5em',
                      }}
                    >
                      <ImportantIcon fill="#0096FF" />
                    </ListItemIcon>
                  </MenuItem>
                  <MenuItem value={InfoType.Info}>
                    <ListItemIcon
                      style={{
                        width: '1em',
                        height: '1em',
                        fontSize: '1.5em',
                      }}
                    >
                      <InfoIcon fill="#0096FF" fill-rule="nonzero" />
                    </ListItemIcon>
                  </MenuItem>
                </Select>
                <SelectDate
                  label={t('attributes.untilDate')}
                  currentDate={props.values.createUntilDate}
                  onDateSelected={(dt) => {
                    props.setFieldValue('createUntilDate', dt);
                  }}
                  minDateValue={dateForAddInfo}
                />
                <Box sx={{ minWidth: 260, flex: '0 0 auto' }}>
                  <TagAutocomplete
                    id="report-info-tags"
                    label={t('attributes.tags') || 'Tags'}
                    typeKey="report-info"
                    value={tags}
                    onChange={setTags}
                    size="small"
                    sx={{ minWidth: 260 }}
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  paddingTop: '2%',
                  marginTop: '1%',
                  flex: 1,
                  minHeight: 0,
                }}
              >
                <Box
                  component="textarea"
                  id="info"
                  name="info"
                  value={props.values.info}
                  onChange={props.handleChange}
                  placeholder={'Add Info here'}
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    width: '100%',
                    overflow: 'auto',
                    resize: 'none',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1.5,
                    fontFamily: 'inherit',
                    fontSize: '0.875rem',
                    lineHeight: 1.5,
                    outline: 'none',
                    '&:focus': {
                      borderColor: 'primary.main',
                      boxShadow: (theme) =>
                        `0 0 0 1px ${theme.palette.primary.main} inset`,
                    },
                  }}
                />
              </Box>
            </DialogContent>
            <DialogActions
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                paddingBottom: '1%',
              }}
            >
              <Box>
                <Button type="submit" variant="contained">
                  {t('button.add')}
                </Button>
              </Box>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  );
}
